import { Injectable } from '@angular/core';
import { Firestore, doc, getDoc, setDoc, deleteDoc, onSnapshot } from '@angular/fire/firestore';
import { Observable, BehaviorSubject } from 'rxjs';
import { FirebaseAuthService } from './firebase-auth.service';

export interface SignupSessionData {
  // User identity (no credentials — passwords are never collected or stored;
  // the user sets them up in the portal via the invite email)
  userEmail?: string;
  userLocale?: string;
  agreeTerms?: boolean;

  // Business information (field names match VenueData / LocalStorageKey values)
  venueName?: string;
  venueCategoryType?: string;
  venueType?: string;

  // Plan selection
  selectedPlan?: string;
  billingFrequency?: string;

  // Contact information
  contactPerson?: string;
  contactPhoneNumber?: string;
  contactEmail?: string;
  streetAddress?: string;
  city?: string;
  stateProvince?: string;
  postalCode?: string;
  country?: string;
  ownerPhoneNumber?: string;

  // Additional data
  internetAvailability?: boolean;

  // Venue profile (collected on the venue step; not part of VenueData —
  // used for plan recommendation and admin context only)
  nonprofitStatus?: string;
  monthlyOrderVolume?: string;

  // Campaign attribution (first-touch UTM parameters)
  utmSource?: string;
  utmMedium?: string;
  utmCampaign?: string;
  utmTerm?: string;
  utmContent?: string;

  // Metadata
  createdAt?: string;
  updatedAt?: string;
  currentStep?: string;
}

export type SyncAuditStatus = 'attempt' | 'success' | 'failure' | 'skipped';

export interface SyncAuditEntry {
  timestamp: Date;
  operation: 'load' | 'write' | 'delete';
  fields: string[];   // field names only — values are never recorded (PII)
  status: SyncAuditStatus;
  detail?: string;    // error code/message, or the reason a sync was skipped
  durationMs?: number;
}

const SYNC_AUDIT_MAX_ENTRIES = 100;

@Injectable({
  providedIn: 'root'
})
export class FirestoreStorageService {
  private readonly COLLECTION = 'signupSessions';
  private syncStatus$ = new BehaviorSubject<'idle' | 'syncing' | 'error'>('idle');
  private lastSyncError$ = new BehaviorSubject<Error | null>(null);
  private syncAuditSubject = new BehaviorSubject<SyncAuditEntry[]>([]);
  private createdAtEnsured = false;

  constructor(
    private firestore: Firestore,
    private authService: FirebaseAuthService
  ) {}

  /**
   * Record a sync audit entry (newest first, capped ring buffer).
   * Powers the debug panel's Sync tab; never stores field values.
   */
  private recordAudit(entry: Omit<SyncAuditEntry, 'timestamp'>): void {
    const entries = [
      { ...entry, timestamp: new Date() },
      ...this.syncAuditSubject.value
    ].slice(0, SYNC_AUDIT_MAX_ENTRIES);
    this.syncAuditSubject.next(entries);
  }

  /**
   * Sync audit log as Observable (newest first) — for the debug panel
   */
  getSyncAudit$(): Observable<SyncAuditEntry[]> {
    return this.syncAuditSubject.asObservable();
  }

  /**
   * Clear the sync audit log
   */
  clearSyncAudit(): void {
    this.syncAuditSubject.next([]);
  }

  /**
   * Get the document reference for current user's signup session
   */
  private getUserDocRef() {
    const userId = this.authService.getCurrentUserId();
    if (!userId) {
      throw new Error('No authenticated user found');
    }
    return doc(this.firestore, `${this.COLLECTION}/${userId}`);
  }

  /**
   * Stamp createdAt on the session's first write when the doc doesn't already
   * have one. Checked at most once per app session; merge-safe if two early
   * writes race (both stamps land via merge, last one wins harmlessly).
   */
  private async createdAtStamp(docRef: ReturnType<typeof doc>): Promise<Partial<SignupSessionData>> {
    if (this.createdAtEnsured) {
      return {};
    }
    this.createdAtEnsured = true;
    try {
      const snap = await getDoc(docRef);
      if (!snap.exists() || !(snap.data() as SignupSessionData)?.createdAt) {
        return { createdAt: new Date().toISOString() };
      }
    } catch (error) {
      // Existence probe failed — skip the stamp rather than fail the write
      console.warn('createdAt probe failed, skipping stamp:', error);
    }
    return {};
  }

  /**
   * Subscribe to the current user's session doc. Fires immediately from the
   * local cache (when persistence is on) and again on every change, including
   * latency-compensated local writes. Returns an unsubscribe function.
   * Only the initial snapshot and errors are recorded in the sync audit.
   */
  subscribeToSession(callback: (data: SignupSessionData | null) => void): () => void {
    const startedAt = Date.now();
    this.recordAudit({ operation: 'load', fields: [], status: 'attempt' });

    let docRef;
    try {
      docRef = this.getUserDocRef();
    } catch (error) {
      this.recordAudit({ operation: 'load', fields: [], status: 'skipped', detail: 'No authenticated user' });
      callback(null);
      return () => undefined;
    }

    let first = true;
    return onSnapshot(
      docRef,
      (snap) => {
        const data = snap.exists() ? (snap.data() as SignupSessionData) : null;
        if (first) {
          first = false;
          if (data?.createdAt) {
            this.createdAtEnsured = true;
          }
          this.recordAudit({
            operation: 'load',
            fields: data ? Object.keys(data) : [],
            status: 'success',
            detail: snap.metadata.fromCache ? 'from cache' : undefined,
            durationMs: Date.now() - startedAt
          });
        }
        callback(data);
      },
      (error) => {
        this.lastSyncError$.next(error as Error);
        this.recordAudit({
          operation: 'load', fields: [], status: 'failure',
          detail: this.describeError(error), durationMs: Date.now() - startedAt
        });
        callback(null);
      }
    );
  }

  /**
   * Load signup session data from Firestore
   */
  async loadSession(): Promise<SignupSessionData | null> {
    const startedAt = Date.now();
    this.recordAudit({ operation: 'load', fields: [], status: 'attempt' });
    try {
      const userId = this.authService.getCurrentUserId();
      if (!userId) {
        console.log('No user ID available, skipping Firestore load');
        this.recordAudit({ operation: 'load', fields: [], status: 'skipped', detail: 'No authenticated user' });
        return null;
      }

      this.syncStatus$.next('syncing');
      const docRef = this.getUserDocRef();
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data() as SignupSessionData;
        if (data.createdAt) {
          this.createdAtEnsured = true;
        }
        console.log('Loaded session data from Firestore:', Object.keys(data));
        this.syncStatus$.next('idle');
        this.recordAudit({
          operation: 'load', fields: Object.keys(data), status: 'success',
          durationMs: Date.now() - startedAt
        });
        return data;
      } else {
        console.log('No existing session found in Firestore');
        this.syncStatus$.next('idle');
        this.recordAudit({
          operation: 'load', fields: [], status: 'success',
          detail: 'No existing session doc', durationMs: Date.now() - startedAt
        });
        return null;
      }
    } catch (error) {
      console.error('Error loading session from Firestore:', error);
      this.syncStatus$.next('error');
      this.lastSyncError$.next(error as Error);
      this.recordAudit({
        operation: 'load', fields: [], status: 'failure',
        detail: this.describeError(error), durationMs: Date.now() - startedAt
      });
      return null;
    }
  }

  /**
   * Save a single field to Firestore
   */
  async setField(field: keyof SignupSessionData, value: unknown): Promise<boolean> {
    const startedAt = Date.now();
    this.recordAudit({ operation: 'write', fields: [field], status: 'attempt' });
    try {
      const userId = this.authService.getCurrentUserId();
      if (!userId) {
        console.warn('No user ID available, skipping Firestore write');
        this.recordAudit({ operation: 'write', fields: [field], status: 'skipped', detail: 'No authenticated user' });
        return false;
      }

      const docRef = this.getUserDocRef();
      const updateData: Partial<SignupSessionData> = {
        ...(await this.createdAtStamp(docRef)),
        [field]: value,
        updatedAt: new Date().toISOString()
      };

      // Merge write — no read-modify-write. The old exists-check + plain
      // setDoc raced under the burst of per-field dual-writes and could
      // overwrite the doc down to a single field.
      await setDoc(docRef, updateData, { merge: true });

      console.log(`Firestore: Updated ${field}:`, value);
      this.recordAudit({
        operation: 'write', fields: [field], status: 'success',
        durationMs: Date.now() - startedAt
      });
      return true;
    } catch (error) {
      console.error(`Error setting field ${field} in Firestore:`, error);
      this.lastSyncError$.next(error as Error);
      this.recordAudit({
        operation: 'write', fields: [field], status: 'failure',
        detail: this.describeError(error), durationMs: Date.now() - startedAt
      });
      return false;
    }
  }

  /**
   * Save multiple fields to Firestore at once
   */
  async setFields(fields: Partial<SignupSessionData>): Promise<boolean> {
    const startedAt = Date.now();
    const fieldNames = Object.keys(fields);
    this.recordAudit({ operation: 'write', fields: fieldNames, status: 'attempt' });
    try {
      const userId = this.authService.getCurrentUserId();
      if (!userId) {
        console.warn('No user ID available, skipping Firestore write');
        this.recordAudit({ operation: 'write', fields: fieldNames, status: 'skipped', detail: 'No authenticated user' });
        return false;
      }

      this.syncStatus$.next('syncing');
      const docRef = this.getUserDocRef();
      const updateData: Partial<SignupSessionData> = {
        ...(await this.createdAtStamp(docRef)),
        ...fields,
        updatedAt: new Date().toISOString()
      };

      // Merge write — see setField for why read-modify-write is not safe here
      await setDoc(docRef, updateData, { merge: true });

      console.log('Firestore: Updated multiple fields:', Object.keys(fields));
      this.syncStatus$.next('idle');
      this.recordAudit({
        operation: 'write', fields: fieldNames, status: 'success',
        durationMs: Date.now() - startedAt
      });
      return true;
    } catch (error) {
      console.error('Error setting multiple fields in Firestore:', error);
      this.syncStatus$.next('error');
      this.lastSyncError$.next(error as Error);
      this.recordAudit({
        operation: 'write', fields: fieldNames, status: 'failure',
        detail: this.describeError(error), durationMs: Date.now() - startedAt
      });
      return false;
    }
  }

  /**
   * Delete the current user's signup session
   */
  async deleteSession(): Promise<boolean> {
    const startedAt = Date.now();
    this.recordAudit({ operation: 'delete', fields: [], status: 'attempt' });
    try {
      const userId = this.authService.getCurrentUserId();
      if (!userId) {
        console.warn('No user ID available, skipping Firestore delete');
        this.recordAudit({ operation: 'delete', fields: [], status: 'skipped', detail: 'No authenticated user' });
        return false;
      }

      const docRef = this.getUserDocRef();
      await deleteDoc(docRef);
      console.log('Firestore: Deleted signup session');
      this.recordAudit({
        operation: 'delete', fields: [], status: 'success',
        durationMs: Date.now() - startedAt
      });
      return true;
    } catch (error) {
      console.error('Error deleting session from Firestore:', error);
      this.lastSyncError$.next(error as Error);
      this.recordAudit({
        operation: 'delete', fields: [], status: 'failure',
        detail: this.describeError(error), durationMs: Date.now() - startedAt
      });
      return false;
    }
  }

  /**
   * Compact error description for the audit log (code + message, no stack)
   */
  private describeError(error: unknown): string {
    const err = error as { code?: string; message?: string } | null | undefined;
    const code = err?.code ? `${err.code}: ` : '';
    return `${code}${err?.message || String(error)}`.slice(0, 300);
  }

  /**
   * Get current sync status as Observable
   */
  getSyncStatus$(): Observable<'idle' | 'syncing' | 'error'> {
    return this.syncStatus$.asObservable();
  }

  /**
   * Get last sync error as Observable
   */
  getLastSyncError$(): Observable<Error | null> {
    return this.lastSyncError$.asObservable();
  }

  /**
   * Get full session data for migration/conversion
   */
  async getFullSessionData(): Promise<SignupSessionData | null> {
    return this.loadSession();
  }

  /**
   * Clear error state
   */
  clearError(): void {
    this.lastSyncError$.next(null);
    if (this.syncStatus$.value === 'error') {
      this.syncStatus$.next('idle');
    }
  }
}
